/**
 * Chapter sync controller: local autosave → outbox → push with baseRev → merge on 409.
 * Phase 2: BroadcastChannel tab leader, durable pending conflicts, drain on hydrate.
 */

import { chapterBodyFingerprint } from '../fingerprint';
import {
  applyConflictResolutions,
  threeWayChapterMerge,
} from '../merge/threeWayChapterMerge';
import {
  ensureParagraphIds,
  listChapterParagraphs,
  type ChapterDocContent,
} from '../../chapterParagraphs';
import type { ChapterSyncStorage } from '../storage/idbChapterStore';
import { getChapterSyncStorage } from '../storage/idbChapterStore';
import type {
  ChapterConflictHunk,
  ChapterConflictResolution,
  ChapterDraftRecord,
  ChapterJournalEntry,
  ChapterJournalKind,
  ChapterOutboxItem,
  ChapterSyncFetchRemoteFn,
  ChapterSyncPushFn,
  ChapterSyncStatus,
  ThreeWayMergeResult,
} from '../types';
import { ChapterSyncTabLeader } from './tabLeader';

function parseDoc(contentJson: string): ChapterDocContent {
  try {
    const parsed = JSON.parse(contentJson) as ChapterDocContent;
    return ensureParagraphIds(parsed);
  } catch {
    return ensureParagraphIds({
      type: 'doc',
      content: [{ type: 'paragraph', attrs: { id: '' }, content: [] }],
    });
  }
}

function wordCountFromJson(contentJson: string): number {
  try {
    const doc = parseDoc(contentJson);
    const plain = listChapterParagraphs(doc)
      .map((p) => p.text)
      .join(' ')
      .trim();
    if (!plain) return 0;
    return plain.split(/\s+/).filter(Boolean).length;
  } catch {
    return 0;
  }
}

function newId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `op-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

function isOnline(): boolean {
  if (typeof navigator === 'undefined') return true;
  return navigator.onLine !== false;
}

/** Run retention every Nth journal entry rather than on every append. */
const JOURNAL_PRUNE_EVERY = 25;

export type ChapterSyncControllerOptions = {
  userId: string;
  bookId: string;
  chapterId: string;
  storage?: ChapterSyncStorage;
  push: ChapterSyncPushFn;
  fetchRemote?: ChapterSyncFetchRemoteFn;
  debounceMs?: number;
  /** Disable BroadcastChannel leader (tests / single-surface). Default true in browsers. */
  multiTabLeader?: boolean;
  onStatus?: (status: ChapterSyncStatus, detail?: string) => void;
  onConflict?: (merge: ThreeWayMergeResult, remoteRev: number) => void;
  onSynced?: (info: { rev: number; title: string; contentJson: string; updatedAt: string }) => void;
};

export class ChapterSyncController {
  private readonly userId: string;
  private readonly bookId: string;
  private readonly chapterId: string;
  private readonly storage: ChapterSyncStorage;
  private readonly push: ChapterSyncPushFn;
  private readonly fetchRemote?: ChapterSyncFetchRemoteFn;
  private readonly debounceMs: number;
  private readonly onStatus?: ChapterSyncControllerOptions['onStatus'];
  private readonly onConflict?: ChapterSyncControllerOptions['onConflict'];
  private readonly onSynced?: ChapterSyncControllerOptions['onSynced'];
  private readonly tabLeader: ChapterSyncTabLeader | null;

  private status: ChapterSyncStatus = 'idle';
  private baseRev = 0;
  private baseFingerprint = '';
  private baseTitle = '';
  private baseContentJson = '';
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private pushing = false;
  private pendingConflict: ThreeWayMergeResult | null = null;
  private pendingRemoteRev: number | null = null;
  private destroyed = false;
  private suspended = false;
  private onlineHandler: (() => void) | null = null;

  constructor(options: ChapterSyncControllerOptions) {
    this.userId = options.userId;
    this.bookId = options.bookId;
    this.chapterId = options.chapterId;
    this.storage = options.storage ?? getChapterSyncStorage();
    this.push = options.push;
    this.fetchRemote = options.fetchRemote;
    this.debounceMs = options.debounceMs ?? 400;
    this.onStatus = options.onStatus;
    this.onConflict = options.onConflict;
    this.onSynced = options.onSynced;

    const enableLeader =
      options.multiTabLeader !== false && typeof BroadcastChannel !== 'undefined';
    this.tabLeader = enableLeader
      ? new ChapterSyncTabLeader({
          scopeKey: `${this.userId}::${this.bookId}::${this.chapterId}`,
        })
      : null;

    if (typeof window !== 'undefined') {
      this.onlineHandler = () => {
        void this.drainOutbox();
      };
      window.addEventListener('online', this.onlineHandler);
    }
  }

  getStatus(): ChapterSyncStatus {
    return this.status;
  }

  getPendingConflict(): ThreeWayMergeResult | null {
    return this.pendingConflict;
  }

  /**
   * Pause debounced autosave + outbox drain (e.g. while a manual Save owns the writer).
   * Pending debounce timers are cancelled — call flushLocal first if draft must be kept.
   */
  suspend(): void {
    this.suspended = true;
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
  }

  /** Resume after suspend(). Does not automatically drain — caller decides. */
  resume(): void {
    this.suspended = false;
  }

  isSuspended(): boolean {
    return this.suspended;
  }

  /**
   * Re-seed base from an authoritative save and drop pending pushes for this chapter.
   * Use after a successful manual Save so outbox cannot race with a stale baseRev.
   */
  async acknowledgeAuthoritativeSave(input: {
    title: string;
    contentJson: string;
    rev: number;
  }): Promise<void> {
    if (this.destroyed) return;
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    this.baseRev = typeof input.rev === 'number' && Number.isFinite(input.rev) ? input.rev : 0;
    this.baseTitle = input.title;
    this.baseContentJson = input.contentJson;
    this.baseFingerprint = chapterBodyFingerprint(input.title, input.contentJson);
    this.pendingConflict = null;
    this.pendingRemoteRev = null;

    const existing = await this.storage.listOutbox(this.userId);
    for (const o of existing) {
      if (o.chapterId === this.chapterId && o.bookId === this.bookId) {
        await this.storage.deleteOutbox(o.id);
      }
    }
    await this.storage.deletePendingConflict(this.userId, this.bookId, this.chapterId);
    await this.storage.putDraft({
      bookId: this.bookId,
      chapterId: this.chapterId,
      userId: this.userId,
      title: input.title,
      contentJson: input.contentJson,
      savedAt: Date.now(),
      baseRev: this.baseRev,
      baseFingerprint: this.baseFingerprint,
      baseContentJson: input.contentJson,
      baseTitle: input.title,
      deviceId: await this.storage.getOrCreateDeviceId(),
    });
    this.setStatus('synced');
  }

  /** Seed base revision from server load; restore parked conflict; drain outbox. */
  async hydrateFromServer(input: {
    title: string;
    contentJson: string;
    rev: number;
  }): Promise<void> {
    this.baseRev = typeof input.rev === 'number' && Number.isFinite(input.rev) ? input.rev : 0;
    this.baseTitle = input.title;
    this.baseContentJson = input.contentJson;
    this.baseFingerprint = chapterBodyFingerprint(input.title, input.contentJson);
    this.setStatus('synced');

    // Restore newer local draft if present
    const draft = await this.storage.getDraft(this.userId, this.bookId, this.chapterId);
    if (draft && draft.savedAt > 0) {
      const draftFp = chapterBodyFingerprint(draft.title, draft.contentJson);
      if (draftFp !== this.baseFingerprint) {
        this.setStatus('localSaved');
      }
    }

    // Crash recovery: old parked merges are discarded. Author draft wins; next
    // push overwrites the library instead of trapping the editor in a conflict UI.
    const parked = await this.storage.getPendingConflict(
      this.userId,
      this.bookId,
      this.chapterId
    );
    if (parked) {
      await this.storage.deletePendingConflict(this.userId, this.bookId, this.chapterId);
      if (typeof parked.remoteRev === 'number' && Number.isFinite(parked.remoteRev)) {
        this.baseRev = parked.remoteRev;
      }
    }

    if (isOnline()) {
      void this.drainOutbox();
    }
  }

  /** Return restored draft if it differs from server baseline. */
  async getRestorableDraft(): Promise<ChapterDraftRecord | null> {
    const draft = await this.storage.getDraft(this.userId, this.bookId, this.chapterId);
    if (!draft) return null;
    const draftFp = chapterBodyFingerprint(draft.title, draft.contentJson);
    if (draftFp === this.baseFingerprint) return null;
    return draft;
  }

  /** Debounced local save + enqueue outbox. */
  noteLocalEdit(title: string, contentJson: string): void {
    if (this.destroyed || this.suspended) return;
    this.setStatus('dirty');
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      this.debounceTimer = null;
      if (this.suspended || this.destroyed) return;
      void this.persistLocal(title, contentJson);
    }, this.debounceMs);
  }

  /** Immediate local persist (e.g. before unload). */
  async flushLocal(title: string, contentJson: string): Promise<void> {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    await this.persistLocal(title, contentJson);
  }

  private async persistLocal(
    title: string,
    contentJson: string,
    options?: { drain?: boolean }
  ): Promise<void> {
    if (this.destroyed) return;
    const deviceId = await this.storage.getOrCreateDeviceId();
    const draft: ChapterDraftRecord = {
      bookId: this.bookId,
      chapterId: this.chapterId,
      userId: this.userId,
      title,
      contentJson,
      savedAt: Date.now(),
      baseRev: this.baseRev,
      baseFingerprint: this.baseFingerprint,
      baseContentJson: this.baseContentJson,
      baseTitle: this.baseTitle,
      deviceId,
    };
    await this.storage.putDraft(draft);

    const item: ChapterOutboxItem = {
      id: newId(),
      bookId: this.bookId,
      chapterId: this.chapterId,
      userId: this.userId,
      title,
      contentJson,
      wordCount: wordCountFromJson(contentJson),
      baseRev: this.baseRev,
      attempts: 0,
      nextAttemptAt: Date.now(),
      createdAt: Date.now(),
    };
    // Replace prior pending outbox entries for this chapter
    const existing = await this.storage.listOutbox(this.userId);
    for (const o of existing) {
      if (o.chapterId === this.chapterId && o.bookId === this.bookId) {
        await this.storage.deleteOutbox(o.id);
      }
    }
    await this.storage.enqueueOutbox(item);
    await this.journal('flush', {
      title,
      contentJson,
      wordCount: item.wordCount,
      baseRev: this.baseRev,
    });
    this.setStatus(isOnline() ? 'localSaved' : 'offline');

    if (isOnline() && options?.drain !== false) {
      void this.drainOutbox();
    }
  }

  async drainOutbox(): Promise<void> {
    if (this.destroyed || this.suspended || this.pushing || this.pendingConflict) return;
    if (this.tabLeader && !this.tabLeader.isLeader()) {
      // Follower tab: local draft/outbox still written; leader pushes.
      return;
    }
    if (!isOnline()) {
      this.setStatus('offline');
      return;
    }

    const items = (await this.storage.listOutbox(this.userId)).filter(
      (o) => o.bookId === this.bookId && o.chapterId === this.chapterId && o.nextAttemptAt <= Date.now()
    );
    if (!items.length) return;

    this.pushing = true;
    this.setStatus('pushing');
    try {
      for (const item of items) {
        const result = await this.push({
          bookId: item.bookId,
          chapterId: item.chapterId,
          title: item.title,
          contentJson: item.contentJson,
          wordCount: item.wordCount,
          baseRev: item.baseRev,
        });

        if (result.ok) {
          this.baseRev = result.rev;
          this.baseTitle = result.title;
          this.baseContentJson = result.contentJson;
          this.baseFingerprint = chapterBodyFingerprint(result.title, result.contentJson);
          await this.storage.deleteOutbox(item.id);
          await this.storage.deletePendingConflict(this.userId, this.bookId, this.chapterId);
          await this.storage.putDraft({
            bookId: this.bookId,
            chapterId: this.chapterId,
            userId: this.userId,
            title: result.title,
            contentJson: result.contentJson,
            savedAt: Date.now(),
            baseRev: result.rev,
            baseFingerprint: this.baseFingerprint,
            baseContentJson: result.contentJson,
            baseTitle: result.title,
            deviceId: await this.storage.getOrCreateDeviceId(),
          });
          await this.journal('push_ok', {
            title: result.title,
            contentJson: result.contentJson,
            wordCount: item.wordCount,
            baseRev: item.baseRev,
            rev: result.rev,
          });
          this.onSynced?.({
            rev: result.rev,
            title: result.title,
            contentJson: result.contentJson,
            updatedAt: result.updatedAt,
          });
          this.setStatus('synced');
          continue;
        }

        if (result.status === 409 && 'remote' in result) {
          // Author save wins: retry once with force overwrite. Never merge remote
          // into the live document — that is what wiped chapters and jumped the caret.
          const retry = await this.push({
            bookId: item.bookId,
            chapterId: item.chapterId,
            title: item.title,
            contentJson: item.contentJson,
            wordCount: item.wordCount,
            baseRev: result.remote.rev,
            force: true,
          });
          if (retry.ok) {
            this.baseRev = retry.rev;
            this.baseTitle = retry.title;
            this.baseContentJson = retry.contentJson;
            this.baseFingerprint = chapterBodyFingerprint(retry.title, retry.contentJson);
            await this.storage.deleteOutbox(item.id);
            await this.storage.deletePendingConflict(this.userId, this.bookId, this.chapterId);
            await this.storage.putDraft({
              bookId: this.bookId,
              chapterId: this.chapterId,
              userId: this.userId,
              title: retry.title,
              contentJson: retry.contentJson,
              savedAt: Date.now(),
              baseRev: retry.rev,
              baseFingerprint: this.baseFingerprint,
              baseContentJson: retry.contentJson,
              baseTitle: retry.title,
              deviceId: await this.storage.getOrCreateDeviceId(),
            });
            await this.journal('push_ok', {
              title: retry.title,
              contentJson: retry.contentJson,
              wordCount: item.wordCount,
              baseRev: result.remote.rev,
              rev: retry.rev,
            });
            this.onSynced?.({
              rev: retry.rev,
              title: retry.title,
              contentJson: retry.contentJson,
              updatedAt: retry.updatedAt,
            });
            this.setStatus('synced');
            continue;
          }
          const retryError = 'error' in retry ? retry.error : 'Push failed';
          await this.journal('push_failed', {
            title: item.title,
            contentJson: item.contentJson,
            wordCount: item.wordCount,
            baseRev: result.remote.rev,
            error: retryError,
          });
          this.setStatus('error', retryError);
          return;
        }

        if ('offline' in result && result.offline) {
          await this.journal('offline', {
            title: item.title,
            contentJson: item.contentJson,
            wordCount: item.wordCount,
            baseRev: item.baseRev,
          });
          this.setStatus('offline');
          return;
        }

        const next: ChapterOutboxItem = {
          ...item,
          attempts: item.attempts + 1,
          nextAttemptAt: Date.now() + Math.min(60_000, 1000 * 2 ** Math.min(item.attempts, 5)),
        };
        await this.storage.updateOutbox(next);
        const pushError = 'error' in result ? result.error : 'Push failed';
        await this.journal('push_failed', {
          title: item.title,
          contentJson: item.contentJson,
          wordCount: item.wordCount,
          baseRev: item.baseRev,
          error: pushError,
        });
        this.setStatus('error', pushError);
        return;
      }
    } finally {
      this.pushing = false;
    }
  }

  private async handleConflict(
    item: ChapterOutboxItem,
    remote: { title: string; content: unknown; rev: number; updatedAt: string }
  ): Promise<void> {
    const remoteContentJson =
      typeof remote.content === 'string' ? remote.content : JSON.stringify(remote.content ?? {});
    const draft = await this.storage.getDraft(this.userId, this.bookId, this.chapterId);
    const baseJson = draft?.baseContentJson || this.baseContentJson || item.contentJson;
    const baseTitle = draft?.baseTitle || this.baseTitle;
    const merge = threeWayChapterMerge({
      base: parseDoc(baseJson),
      mine: parseDoc(item.contentJson),
      theirs: parseDoc(remoteContentJson),
      baseTitle,
      mineTitle: item.title,
      theirsTitle: remote.title,
    });

    // Re-base on remote for next push attempt
    this.baseRev = remote.rev;
    this.baseTitle = remote.title;
    this.baseContentJson = remoteContentJson;
    this.baseFingerprint = chapterBodyFingerprint(remote.title, remoteContentJson);

    if (merge.conflicts.length === 0) {
      // Auto-merged — enqueue push against remote rev (caller re-drains after lock release)
      await this.storage.deleteOutbox(item.id);
      await this.storage.deletePendingConflict(this.userId, this.bookId, this.chapterId);
      const mergedJson = JSON.stringify(merge.merged);
      await this.persistLocal(merge.mergedTitle, mergedJson, { drain: false });
      this.onSynced?.({
        rev: remote.rev,
        title: merge.mergedTitle,
        contentJson: mergedJson,
        updatedAt: remote.updatedAt,
      });
      return;
    }

    this.pendingConflict = merge;
    this.pendingRemoteRev = remote.rev;
    await this.storage.putPendingConflict({
      bookId: this.bookId,
      chapterId: this.chapterId,
      userId: this.userId,
      remoteRev: remote.rev,
      mergeJson: JSON.stringify(merge),
      savedAt: Date.now(),
    });
    this.setStatus('conflict');
    this.onConflict?.(merge, remote.rev);
  }

  /**
   * Apply conflict resolutions and push the resolved document.
   */
  async resolveConflicts(resolutions: ChapterConflictResolution[]): Promise<void> {
    if (!this.pendingConflict) return;
    const doc = applyConflictResolutions(this.pendingConflict, resolutions);
    const title = this.pendingConflict.mergedTitle;
    const contentJson = JSON.stringify(doc);
    this.pendingConflict = null;
    this.pendingRemoteRev = null;
    await this.storage.deletePendingConflict(this.userId, this.bookId, this.chapterId);
    await this.persistLocal(title, contentJson);
  }

  /** Keep-all-mine or keep-all-theirs shortcut. */
  async resolveAll(choice: 'mine' | 'theirs'): Promise<void> {
    if (!this.pendingConflict) return;
    const resolutions: ChapterConflictResolution[] = this.pendingConflict.conflicts.map((h) => ({
      hunkId: h.id,
      choice,
    }));
    await this.resolveConflicts(resolutions);
  }

  /**
   * Compare local draft to a polled remote body and merge if needed.
   * Returns merge result when conflicts exist; otherwise applies auto-merge silently.
   */
  async reconcileWithRemote(remote: {
    title: string;
    content: unknown;
    rev: number;
    updatedAt: string;
  }): Promise<{ kind: 'noop' | 'applied' | 'conflict'; merge?: ThreeWayMergeResult; conflicts?: ChapterConflictHunk[] }> {
    const remoteContentJson =
      typeof remote.content === 'string' ? remote.content : JSON.stringify(remote.content ?? {});
    const remoteFp = chapterBodyFingerprint(remote.title, remoteContentJson);

    if (remote.rev === this.baseRev && remoteFp === this.baseFingerprint) {
      return { kind: 'noop' };
    }

    const draft = await this.storage.getDraft(this.userId, this.bookId, this.chapterId);
    const localTitle = draft?.title ?? this.baseTitle;
    const localJson = draft?.contentJson ?? this.baseContentJson;
    const localFp = chapterBodyFingerprint(localTitle, localJson);

    // Clean remote advance, no local dirty
    if (localFp === this.baseFingerprint) {
      this.baseRev = remote.rev;
      this.baseTitle = remote.title;
      this.baseContentJson = remoteContentJson;
      this.baseFingerprint = remoteFp;
      await this.storage.putDraft({
        bookId: this.bookId,
        chapterId: this.chapterId,
        userId: this.userId,
        title: remote.title,
        contentJson: remoteContentJson,
        savedAt: Date.now(),
        baseRev: remote.rev,
        baseFingerprint: remoteFp,
        baseContentJson: remoteContentJson,
        baseTitle: remote.title,
        deviceId: await this.storage.getOrCreateDeviceId(),
      });
      this.setStatus('synced');
      this.onSynced?.({
        rev: remote.rev,
        title: remote.title,
        contentJson: remoteContentJson,
        updatedAt: remote.updatedAt,
      });
      return { kind: 'applied' };
    }

    // Local has author edits. Keep them. Adopt remote rev so the next save can overwrite.
    this.baseRev = remote.rev;
    return { kind: 'noop' };
  }

  destroy(): void {
    this.destroyed = true;
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
    this.tabLeader?.destroy();
    if (this.onlineHandler && typeof window !== 'undefined') {
      window.removeEventListener('online', this.onlineHandler);
      this.onlineHandler = null;
    }
  }

  private setStatus(status: ChapterSyncStatus, detail?: string): void {
    this.status = status;
    this.onStatus?.(status, detail);
  }

  /**
   * Append to the local journal. Best effort by design: a journal write must
   * never be the reason a save fails.
   */
  private async journal(
    kind: ChapterJournalKind,
    input: {
      title: string;
      contentJson: string;
      wordCount?: number;
      baseRev: number;
      rev?: number;
      error?: string;
    }
  ): Promise<void> {
    try {
      const appended = await this.storage.appendJournal({
        bookId: this.bookId,
        chapterId: this.chapterId,
        userId: this.userId,
        kind,
        title: input.title,
        contentJson: input.contentJson,
        wordCount: input.wordCount,
        baseRev: input.baseRev,
        rev: input.rev,
        error: input.error,
        deviceId: await this.storage.getOrCreateDeviceId(),
      });
      if (appended && appended.seq % JOURNAL_PRUNE_EVERY === 0) {
        await this.storage.pruneJournal(this.userId, this.bookId, this.chapterId);
      }
    } catch {
      // ignore
    }
  }

  /** Everything this device knows happened to this chapter, oldest first. */
  async listJournal(): Promise<ChapterJournalEntry[]> {
    try {
      return await this.storage.listJournalForChapter(this.userId, this.bookId, this.chapterId);
    } catch {
      return [];
    }
  }
}

export async function migrateWebLocalStorageDrafts(userId: string): Promise<number> {
  const storage = getChapterSyncStorage();
  if (storage.migrateLocalStorageDrafts) {
    return storage.migrateLocalStorageDrafts(userId);
  }
  return 0;
}
